import type {
  MicCheckHost,
  MicReport,
  ProblemCause,
  ProblemLayer,
  ResolvedBy,
} from './types'

/** The Soundcheck implementation version stamped on every telemetry row. */
export const SOUNDCHECK_VERSION = '2'

/**
 * The evidence a confirmed problem carries into telemetry. Transcript problems
 * set notListenedMS or speechStartsSinceCaption, depending on which check
 * confirmed them.
 */
export type ProblemEvidence = {
  // Milliseconds since a heartbeat last listed the mic (or since it became
  // ready), for not_listened.
  notListenedMS?: number
  // Worker speech starts since the last own caption, for no_captions.
  speechStartsSinceCaption?: number
  // Milliseconds since the last own caption, and since LiveKit last named the
  // student an active speaker; null when it never did. The LiveKit age shows
  // whether audio reached LiveKit, to place a failure after the fact.
  ownCaptionAgeMS?: number | null
  serverSpeakingAgeMS?: number | null
}

/**
 * Everything Soundcheck records. The controller calls these; the concrete
 * implementation adds the meeting envelope and routes through
 * `meeting.logEventPassive` so automated health reporting never extends the
 * student's idle timeout. Tests inject a recording stub.
 */
export type SoundcheckTelemetry = {
  micResult: (
    host: MicCheckHost,
    report: MicReport,
    msToResult: number | null
  ) => void
  // host: the meeting controller, or a preview host (the AV check) that logs
  // its own MicCheck failures.
  problem: (
    layer: ProblemLayer,
    cause: ProblemCause,
    shown: boolean,
    evidence: ProblemEvidence,
    host: MicCheckHost
  ) => void
  resolved: (
    layer: ProblemLayer,
    cause: ProblemCause,
    secondsOpen: number,
    resolvedBy: ResolvedBy,
    host: MicCheckHost
  ) => void
  incidentEnded: (
    reason: 'recovered' | 'left',
    incidentSeconds: number,
    openCauses: string,
    host: MicCheckHost
  ) => void
  dialogShown: (
    cause: ProblemCause,
    source: 'auto' | 'banner' | 'notifications_enabled'
  ) => void
  dialogDismissed: (cause: ProblemCause, incidentSeconds: number) => void
  repairResult: (
    cause: ProblemCause,
    republished: boolean,
    errorName: string,
    errorMessageSanitized: string
  ) => void
}

/** A telemetry sink that does nothing, for hosts without a meeting. */
export const NOOP_TELEMETRY: SoundcheckTelemetry = {
  micResult: () => {},
  problem: () => {},
  resolved: () => {},
  incidentEnded: () => {},
  dialogShown: () => {},
  dialogDismissed: () => {},
  repairResult: () => {},
}

/** The passive logger the concrete telemetry writes through. */
export type PassiveLogger = (
  name: string,
  params?: Record<string, unknown>
) => void

/**
 * Build a telemetry sink bound to a meeting's passive logger. `envelope`
 * supplies the fields common to every row that the caller can compute lazily:
 * the current track SID and mic state.
 */
export function createSoundcheckTelemetry(
  logEventPassive: PassiveLogger,
  envelope: () => { trackSid: string | null; micState: string }
): SoundcheckTelemetry {
  const base = () => {
    const { trackSid, micState } = envelope()
    return {
      soundcheck_version: SOUNDCHECK_VERSION,
      track_sid: trackSid ?? '',
      mic_state: micState,
    }
  }

  return {
    micResult: (host, report, msToResult) => {
      logEventPassive('soundcheck_mic_result', {
        ...base(),
        host,
        state: report.state,
        reason:
          report.state === 'failed'
            ? report.cause
            : report.state === 'checking' || report.state === 'inactive'
              ? report.reason
              : '',
        speech_detection:
          report.state === 'ready' ? report.speechDetection : '',
        has_heard_speech:
          report.state === 'ready' ? String(report.hasHeardSpeech) : '',
        ms_to_result: msToResult ?? '',
      })
    },
    problem: (layer, cause, shown, evidence, host) => {
      // GA4 drops parameters past 25 on an event, and the meeting and tab
      // envelope adds 7. Unset evidence is left out rather than sent empty.
      const evidenceParams = withoutEmpty({
        not_listened_ms: evidence.notListenedMS ?? '',
        speech_starts_since_caption: evidence.speechStartsSinceCaption ?? '',
        own_caption_age_ms: evidence.ownCaptionAgeMS ?? '',
        server_speaking_age_ms: evidence.serverSpeakingAgeMS ?? '',
      })
      logEventPassive('soundcheck_problem', {
        ...base(),
        host,
        cause,
        layer,
        shown: String(shown),
        ...evidenceParams,
      })
    },
    resolved: (layer, cause, secondsOpen, resolvedBy, host) => {
      logEventPassive('soundcheck_resolved', {
        ...base(),
        host,
        cause,
        layer,
        seconds_open: secondsOpen,
        resolved_by: resolvedBy,
      })
    },
    incidentEnded: (reason, incidentSeconds, openCauses, host) => {
      logEventPassive('soundcheck_incident_ended', {
        ...base(),
        host,
        reason,
        incident_seconds: incidentSeconds,
        open_causes: openCauses,
      })
    },
    dialogShown: (cause, source) => {
      logEventPassive('soundcheck_dialog_shown', { ...base(), cause, source })
    },
    dialogDismissed: (cause, incidentSeconds) => {
      logEventPassive('soundcheck_dialog_dismissed', {
        ...base(),
        cause,
        incident_seconds: incidentSeconds,
      })
    },
    repairResult: (cause, republished, errorName, errorMessageSanitized) => {
      logEventPassive('soundcheck_repair_result', {
        ...base(),
        cause,
        republished: String(republished),
        error_name: errorName,
        error_message_sanitized: errorMessageSanitized,
      })
    },
  }
}

function withoutEmpty(
  params: Record<string, string | number>
): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== '')
  )
}
